import { computed } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { IconDefinition as SolidIcon } from '@fortawesome/pro-solid-svg-icons'
import type { AppTextColor } from '@shared/ui/typography'

import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'

interface UseAppSelectClassesOptions {
  isOpen: Readonly<Ref<boolean>>
  isDisabled: Readonly<Ref<boolean>>
  isError: Readonly<Ref<boolean>>
  isSuccess: Readonly<Ref<boolean>>
}

interface UseAppSelectClassesReturn {
  rootClasses: ComputedRef<(string | Record<string, boolean>)[]>
  triggerClasses: ComputedRef<(string | Record<string, boolean>)[]>
  messageColor: ComputedRef<AppTextColor>
  chevronIcon: ComputedRef<SolidIcon>
}

/**
 * Default-skin view helper for the `app-select__*` design. Maps the headless
 * core's semantic state (isOpen / isDisabled / isError / isSuccess) onto this
 * skin's BEM classes, message-color token and chevron icon.
 *
 * This is the view layer — `useAppSelectCore` is skin-agnostic and returns none
 * of these. A different skin builds its own classes / colors / icons (inline in
 * its SFC, or via its own helper) over the same unchanged core + wrapper hooks.
 */
export const useAppSelectClasses = (
  options: UseAppSelectClassesOptions,
): UseAppSelectClassesReturn => {
  const { isOpen, isDisabled, isError, isSuccess } = options

  const rootClasses = computed<(string | Record<string, boolean>)[]>(() => [
    'app-select',
    { 'app-select--disabled': isDisabled.value },
  ])

  const triggerClasses = computed<(string | Record<string, boolean>)[]>(() => [
    'app-select__trigger',
    {
      'app-select__trigger--open': isOpen.value,
      'app-select__trigger--disabled': isDisabled.value,
      'app-select__trigger--success': isSuccess.value,
      'app-select__trigger--error': isError.value,
    },
  ])

  const messageColor = computed<AppTextColor>(() => {
    if (isError.value) return 'red-600'
    return 'slate-500'
  })

  const chevronIcon = computed<SolidIcon>(() => isOpen.value ? faChevronUp : faChevronDown)

  return {
    rootClasses,
    triggerClasses,
    messageColor,
    chevronIcon,
  }
}
